import { useState, useCallback, useMemo } from 'react';
import {
    DataTableColumn,
    SortModel,
    SortDirection,
    ColumnWidths,
    ExpandedRows,
    SelectionModel,
    NestedRowData,
} from '../types';

/**
 * Helper function to get consistent row identifier for selection
 * Prioritizes _id, then id, then falls back to index for non-checkbox scenarios
 */
const getRowSelectIdentifier = (
    row: any,
    index: number,
    checkboxSelection: boolean = false,
    treeData: boolean = false
): string | number => {
    if (checkboxSelection || treeData) {
        // For checkbox selection or tree data, always use stable identifier
        return row._id ?? row.id ?? `row-${index}`;
    }
    // For non-checkbox scenarios, can use index
    return index;
};

interface UseDataTableStateProps<T = any> {
    rows: T[] | NestedRowData[];
    columns: DataTableColumn<T>[];
    sortModel?: SortModel[];
    defaultSortModel?: SortModel[];
    onSortModelChange?: (model: SortModel[]) => void;
    columnWidths?: ColumnWidths;
    onColumnWidthsChange?: (widths: ColumnWidths) => void;
    expandedRows?: ExpandedRows;
    onExpandedRowsChange?: (expanded: ExpandedRows) => void;
    selectionModel?: SelectionModel;
    onSelectionModelChange?: (selection: SelectionModel) => void;
    treeData?: boolean;
    persistColumnWidths?: boolean;
    columnWidthsStorageKey?: string;
    checkboxSelection?: boolean;
}

export const useDataTableState = <T extends Record<string, any> = any>({
    rows,
    columns,
    sortModel,
    defaultSortModel,
    onSortModelChange,
    columnWidths,
    onColumnWidthsChange,
    expandedRows,
    onExpandedRowsChange,
    selectionModel,
    onSelectionModelChange,
    treeData = false,
    persistColumnWidths = false,
    columnWidthsStorageKey = 'datatable-column-widths',
    checkboxSelection = false,
}: UseDataTableStateProps<T>) => {
    // Initialize sort state
    const [internalSortModel, setInternalSortModel] = useState<SortModel[]>(
        defaultSortModel || []
    );
    const currentSortModel = sortModel || internalSortModel;

    // Initialize column widths
    const [internalColumnWidths, setInternalColumnWidths] = useState<ColumnWidths>(() => {
        const initialWidths: ColumnWidths = {};

        // Load from localStorage if persistence is enabled
        if (persistColumnWidths && typeof window !== 'undefined') {
            try {
                const stored = localStorage.getItem(columnWidthsStorageKey);
                if (stored) {
                    const parsedWidths = JSON.parse(stored);
                    Object.assign(initialWidths, parsedWidths);
                }
            } catch (error) {
                console.warn('Failed to load column widths from localStorage:', error);
            }
        }

        // Set explicit widths only for columns that have them defined or have been resized
        columns.forEach((column) => {
            // Only set initial width if it was stored, or if the column has an explicit width/minWidth
            if (initialWidths[column.id]) {
                // Keep the stored width
                return;
            }
            
            if (typeof column.width === 'number') {
                initialWidths[column.id] = column.width;
                return;
            }
            
            if (typeof column.minWidth === 'number') {
                initialWidths[column.id] = column.minWidth;
                return;
            }
            
            // For flexible columns, don't set an initial width
            // This allows them to fill remaining space
        });

        return initialWidths;
    });
    const currentColumnWidths = columnWidths || internalColumnWidths;

    // Initialize expanded rows state
    const [internalExpandedRows, setInternalExpandedRows] = useState<ExpandedRows>({});
    const currentExpandedRows = expandedRows || internalExpandedRows;

    // Initialize selection state
    const [internalSelectionModel, setInternalSelectionModel] = useState<SelectionModel>({
        selectedRows: [],
        isAllSelected: false,
        isSomeSelected: false,
    });
    const currentSelectionModel = selectionModel || internalSelectionModel;

    // Update selection model calculations
    const updateSelectionModel = useCallback((selectedRows: (string | number)[]) => {
        const totalRows = rows.length;
        const isAllSelected = totalRows > 0 && selectedRows.length === totalRows;
        const isSomeSelected = selectedRows.length > 0 && selectedRows.length < totalRows;

        const newSelectionModel: SelectionModel = {
            selectedRows,
            isAllSelected,
            isSomeSelected,
        };

        if (!selectionModel) {
            setInternalSelectionModel(newSelectionModel);
        }
        onSelectionModelChange?.(newSelectionModel);
    }, [rows.length, selectionModel, onSelectionModelChange]);

    // Sort function
    const sortData = useCallback((data: T[] | NestedRowData[], sortModel: SortModel[]) => {
        if (sortModel.length === 0) return data;

        const sortedData = [...data];

        sortedData.sort((a, b) => {
            for (const sort of sortModel) {
                const column = columns.find(col => col.id === sort.field);
                if (!column || !sort.sort) continue;

                // Get values to compare
                const aValue = column.field ?
                    (treeData ? (a as NestedRowData).data[column.field] : (a as T)[column.field]) :
                    undefined;
                const bValue = column.field ?
                    (treeData ? (b as NestedRowData).data[column.field] : (b as T)[column.field]) :
                    undefined;

                // Use custom comparator if provided
                let result: number;
                if (column.sortComparator) {
                    result = column.sortComparator(aValue, bValue);
                } else {
                    // Default comparison
                    if (aValue < bValue) result = -1;
                    else if (aValue > bValue) result = 1;
                    else result = 0;
                }

                if (result !== 0) {
                    return sort.sort === 'desc' ? -result : result;
                }
            }
            return 0;
        });

        return sortedData as typeof data;
    }, [columns, treeData]);

    // Sorted rows
    const sortedRows = useMemo(() => {
        return sortData(rows, currentSortModel);
    }, [rows, currentSortModel, sortData]);

    // Handle direct sort (used by mobile sort select — sets field+direction in one call)
    const handleDirectSort = useCallback((field: string, direction: SortDirection) => {
        const newSortModel: SortModel[] = direction ? [{ field, sort: direction }] : [];
        if (!sortModel) {
            setInternalSortModel(newSortModel);
        }
        onSortModelChange?.(newSortModel);
    }, [sortModel, onSortModelChange]);

    // Handle sort
    const handleSort = useCallback((columnId: string) => {
        const column = columns.find(col => col.id === columnId);
        if (!column?.sortable) return;

        const existingSortIndex = currentSortModel.findIndex(s => s.field === columnId);
        let newSortModel: SortModel[] = [];

        if (existingSortIndex >= 0) {
            // Update existing sort
            const currentSort = currentSortModel[existingSortIndex];
            const nextDirection: SortDirection =
                currentSort.sort === 'desc' ? 'asc' :
                    currentSort.sort === 'asc' ? null : 'desc';

            if (nextDirection) {
                newSortModel = [
                    ...currentSortModel.slice(0, existingSortIndex),
                    { ...currentSort, sort: nextDirection },
                    ...currentSortModel.slice(existingSortIndex + 1),
                ];
            } else {
                // Remove sort
                newSortModel = [
                    ...currentSortModel.slice(0, existingSortIndex),
                    ...currentSortModel.slice(existingSortIndex + 1),
                ];
            }
        } else {
            // Add new sort
            newSortModel = [{ field: columnId, sort: 'desc' }];
        }

        if (!sortModel) {
            setInternalSortModel(newSortModel);
        }
        onSortModelChange?.(newSortModel);
    }, [columns, currentSortModel, sortModel, onSortModelChange]);

    // Handle column resize
    const handleColumnResize = useCallback((columnId: string, width: number) => {
        const newWidths = { ...currentColumnWidths, [columnId]: width };
        if (!columnWidths) {
            setInternalColumnWidths(newWidths);
        }
        onColumnWidthsChange?.(newWidths);

        // Persist to localStorage if enabled
        if (persistColumnWidths && typeof window !== 'undefined') {
            try {
                localStorage.setItem(columnWidthsStorageKey, JSON.stringify(newWidths));
            } catch (error) {
                console.warn('Failed to save column widths to localStorage:', error);
            }
        }
    }, [
        currentColumnWidths,
        columnWidths,
        onColumnWidthsChange,
        persistColumnWidths,
        columnWidthsStorageKey,
    ]);

    // Handle row expand
    const handleRowExpand = useCallback((rowId: string | number) => {
        const newExpandedRows = {
            ...currentExpandedRows,
            [rowId]: !currentExpandedRows[rowId],
        };

        if (!expandedRows) {
            setInternalExpandedRows(newExpandedRows);
        }
        onExpandedRowsChange?.(newExpandedRows);
    }, [currentExpandedRows, expandedRows, onExpandedRowsChange]);

    // Handle row selection
    const handleRowSelect = useCallback((rowId: string | number) => {
        const selectedRows = [...currentSelectionModel.selectedRows];
        const index = selectedRows.indexOf(rowId);

        if (index >= 0) {
            selectedRows.splice(index, 1);
        } else {
            selectedRows.push(rowId);
        }

        updateSelectionModel(selectedRows);
    }, [currentSelectionModel.selectedRows, updateSelectionModel]);

    // Handle select all
    const handleSelectAll = useCallback(() => {
        const allRowIds = rows.map((row, index) =>
            getRowSelectIdentifier(row, index, checkboxSelection, treeData)
        );

        const selectedRows = currentSelectionModel.isAllSelected ? [] : allRowIds;

        console.log("selectedRows", selectedRows);
        updateSelectionModel(selectedRows);
    }, [
        rows,
        currentSelectionModel.isAllSelected,
        updateSelectionModel,
        treeData,
        checkboxSelection,
    ]);

    return {
        sortedRows,
        currentSortModel,
        currentColumnWidths,
        currentExpandedRows,
        currentSelectionModel,
        handleSort,
        handleDirectSort,
        handleColumnResize,
        handleRowExpand,
        handleRowSelect,
        handleSelectAll,
    };
};

export default useDataTableState;
